import { useMemo } from 'react';
import { useFetchDataWithFilters } from 'hooks';
import { Closure, ClosureStatusEnum, Event, SortingCenter } from 'types';

interface QueryParams {
  [key: string]: string;
}

export const useLatestClosure = (additionalQueryParams: QueryParams = {}): [Closure | null, boolean] => {
  const params: QueryParams = {
    status: ClosureStatusEnum.CLOSED,
    ...additionalQueryParams,
  };
  const state = useFetchDataWithFilters('/closures', params, 1, 1, true, Event.REFRESH_LATEST_CLOSURE);

  if (state.loading) {
    return [null, true];
  }

  if (!state.value) {
    return [null, false];
  }

  return [state.value.results?.[0], false];
};

export const useEarliestClosure = (): [Closure | null, boolean] => {
  const params: QueryParams = {
    status: ClosureStatusEnum.CLOSED,
    'order[year]': 'asc',
    'order[month]': 'asc',
  };
  const state = useFetchDataWithFilters('/closures', params, 1, 1);

  if (state.loading) {
    return [null, true];
  }

  if (!state.value) {
    return [null, false];
  }

  return [state.value.results?.[0], false];
};

export const useLatestYearlyClosure = (): [Closure | null, boolean] => {
  const params: QueryParams = {
    status: ClosureStatusEnum.CLOSED,
    month: '12',
  };
  const state = useFetchDataWithFilters('/closures', params, 1, 1);

  if (state.loading) {
    return [null, true];
  }

  if (!state.value) {
    return [null, false];
  }

  return [state.value.results?.[0], false];
};

interface FirstLastClosureDateBoundaries {
  firstClosureDate: Date;
  lastClosureDate: Date;
}

export const useFirstAndLastClosureDates = (): FirstLastClosureDateBoundaries | null => {
  const [lastClosure] = useLatestClosure();
  const [firstClosure] = useEarliestClosure();

  return firstClosure && lastClosure
    ? {
        firstClosureDate: new Date(firstClosure.year, firstClosure.month - 1),
        lastClosureDate: new Date(lastClosure.year, lastClosure.month - 1),
      }
    : null;
};

export const useLatestClosureDate = (sortingCenter: SortingCenter | null | undefined): [Date | null, Date | null] => {
  const [closure] = useLatestClosure();
  const delayMonths = 2;

  let twoMonthAfterClosureDate = null;
  let closureDate = null;

  // below the day equals to 0 => the last day of the previous month
  if (!!closure && closure?.year && closure?.month) {
    twoMonthAfterClosureDate = new Date(closure?.year, closure?.month + delayMonths, 0);
    closureDate = new Date(closure?.year, closure?.month, 0);
  } else if (sortingCenter?.initializationDate) {
    // if there are no closures in the sorting center yet, we count two months
    // starting from the date of the sorting center initialization
    const initializationDate = new Date(sortingCenter.initializationDate);
    twoMonthAfterClosureDate = new Date(
      initializationDate.getFullYear(),
      initializationDate.getMonth() + delayMonths + 1,
      0,
    );
    closureDate = new Date(sortingCenter.initializationDate);
  }
  return [closureDate, twoMonthAfterClosureDate];
};

export const useLatestMonthAndYear = (
  sortingCenter: SortingCenter | null | undefined,
  latestClosure: Closure | null,
  isClosureLoading: boolean = false,
): [number, number] | [null, null] => {
  if (latestClosure) {
    return [latestClosure.month, latestClosure.year];
  }

  if (!isClosureLoading && sortingCenter) {
    const initializationDate = new Date(sortingCenter.initializationDate);
    const initializationMonth = initializationDate.getMonth() + 1;
    const initializationYear = initializationDate.getFullYear();
    return [initializationMonth, initializationYear];
  }

  return [null, null];
};

export const useMonthAndYearToClose = (
  sortingCenter: SortingCenter | null | undefined,
  latestClosure: Closure | null,
  isClosureLoading: boolean = false,
): [number, number] | [null, null] => {
  const [latestMonth, latestYear] = useLatestMonthAndYear(sortingCenter, latestClosure, isClosureLoading);

  return useMemo(() => {
    if (latestMonth && latestYear) {
      const month = (latestMonth % 12) + 1;
      const year = latestMonth === 12 ? latestYear + 1 : latestYear;
      return [month, year];
    }

    return [null, null];
  }, [latestMonth, latestYear]);
};
